import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { messageRoutesApi } from '@/services/api';
import type { CreateMessageRouteInput, UpdateMessageRouteInput, MessageRouteFilters } from '@/types';

export const useMessageRoutes = (page = 1, limit = 20, filters?: MessageRouteFilters) => {
  return useQuery({
    queryKey: ['message-routes', page, limit, filters],
    queryFn: () => messageRoutesApi.getAll(page, limit, filters).then(res => res.data),
  });
};

export const useMessageRoute = (id: string | undefined) => {
  return useQuery({
    queryKey: ['message-route', id],
    queryFn: () => messageRoutesApi.getById(id!).then(res => res.data.data),
    enabled: !!id,
  });
};

export const useCreateMessageRoute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMessageRouteInput) =>
      messageRoutesApi.create(data).then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-routes'] });
    },
  });
};

export const useUpdateMessageRoute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMessageRouteInput }) =>
      messageRoutesApi.update(id, data).then(res => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['message-routes'] });
      queryClient.invalidateQueries({ queryKey: ['message-route', variables.id] });
    },
  });
};

export const useDeleteMessageRoute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => messageRoutesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-routes'] });
    },
  });
};
